import React from 'react'
import { Link } from '@inertiajs/react'
import { ArrowRight, CalendarDays, ChevronLeft, ChevronRight, Eye } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { buttonVariants } from '@/components/ui/button'
import { signedMoney } from '@/components/dashboard/Delta'
import DemandChip from '@/components/market/DemandChip'
import NightLineup from '@/components/market/NightLineup'
import { formatMoney } from '@/lib/format'
import { nightsUntil } from '@/lib/calendar-utils'
import { nightsAway } from '@/lib/plain-language'
import { TONE_STYLE } from '@/lib/market'
import { cn } from '@/lib/utils'
import type { MarketAttentionItem, MarketCompetitor, MarketNight, MarketPriceDomain } from '@/types/market'

/**
 * One night, opened: what the engine is asking, where that sits against the compset, the server's
 * one-sentence verdict, and the night's lineup. The page never changes a price; its one action
 * opens the Prices calendar on that month.
 */

function Figure({
  label,
  children,
  sub,
  title,
  className,
}: {
  label: string
  children: React.ReactNode
  sub?: React.ReactNode
  title?: string
  className?: string
}) {
  return (
    <div className={cn('min-w-0 bg-white px-3.5 py-2.5', className)} title={title}>
      <dt className="truncate text-[11px] font-medium text-muted-foreground" title={label}>
        {label}
      </dt>
      <dd className="mt-0.5 text-base font-semibold leading-snug tabular-nums text-foreground">{children}</dd>
      {sub && <dd className="text-[11px] tabular-nums text-muted-foreground">{sub}</dd>}
    </div>
  )
}

function NavButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: (() => void) | null
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={onClick == null}
      onClick={onClick ?? undefined}
      className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-border/60 bg-white text-muted-foreground shadow-sm transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  )
}

export interface NightShelfProps {
  night: MarketNight
  competitors: MarketCompetitor[]
  numbers: Map<number, number>
  youName: string
  currency: string
  today: string
  domain: MarketPriceDomain
  attentionItem?: MarketAttentionItem
  /** The single priced competitor's name on a night without a market (one priced). */
  singleName: string | null
  onPrev: (() => void) | null
  onNext: (() => void) | null
  onLineupKeyDown?: (event: React.KeyboardEvent) => void
  reducedMotion?: boolean
  /** In the phone sheet the night's name is the sheet's own title. */
  hideTitle?: boolean
}

export default function NightShelf({
  night,
  competitors,
  numbers,
  youName,
  currency,
  today,
  domain,
  attentionItem,
  singleName,
  onPrev,
  onNext,
  onLineupKeyDown,
  reducedMotion = false,
  hideTitle = false,
}: NightShelfProps) {
  const money = (v: number) => formatMoney(v, currency)
  const tone = attentionItem?.tone ?? 'neutral'
  const toneStyle = TONE_STYLE[tone]
  const away = night.isToday ? null : nightsAway(nightsUntil(night.date, today))
  const showBookingCom = night.yourBookingComPrice != null || night.yourBookingComSoldOut

  return (
    <section aria-label={`Night of ${night.label}`} className="space-y-4" data-testid="night-shelf">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {!hideTitle && (
            <h3 className="text-base font-semibold tracking-tight text-foreground">
              {night.isToday ? `Tonight, ${night.label}` : night.label}
            </h3>
          )}
          <div className={cn('flex flex-wrap items-center gap-1.5', !hideTitle && 'mt-1')}>
            {away && <span className="mr-1 text-xs text-muted-foreground">{away}</span>}
            <DemandChip
              demand={night.marketDemand}
              label={night.marketDemandLabel}
              sentence={night.marketDemandSentence}
            />
            {night.isWeekend && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                Weekend
              </span>
            )}
            {night.eventName && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-medium text-white">
                <CalendarDays className="h-3 w-3" />
                {night.eventName}
              </span>
            )}
            {attentionItem && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset',
                  toneStyle.soft,
                  toneStyle.text,
                  toneStyle.ring
                )}
              >
                <Eye className="h-3 w-3" />
                Worth a look
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <NavButton label="Previous night" onClick={onPrev}>
            <ChevronLeft className="h-4 w-4" />
          </NavButton>
          <NavButton label="Next night" onClick={onNext}>
            <ChevronRight className="h-4 w-4" />
          </NavButton>
          <Link
            href={night.pricesHref}
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'ml-1 h-8 rounded-lg px-3 text-xs')}
          >
            Open prices
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {/* The verdict: the server's sentence, verbatim, tinted only by a "worth a look" judgement. */}
      <div className={cn('rounded-lg border-l-[3px] px-4 py-3', toneStyle.soft, toneStyle.edge)}>
        <p className="text-sm leading-relaxed text-foreground" data-testid="night-verdict">
          {night.verdict}
        </p>
      </div>

      <dl
        className={cn(
          'grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border/60 bg-border/40',
          showBookingCom ? 'sm:grid-cols-3 lg:grid-cols-6' : 'sm:grid-cols-5'
        )}
      >
        <Figure label="Priced at">
          {night.yourPrice == null ? (
            <span className="text-sm font-normal text-muted-foreground">Not priced</span>
          ) : (
            <span className="text-primary">{money(night.yourPrice)}</span>
          )}
        </Figure>
        <Figure label={night.marketClaim ? 'Compset median' : (singleName ?? 'Compset median')}>
          {night.compsetMedian == null ? (
            <span className="text-sm font-normal text-muted-foreground">--</span>
          ) : (
            money(night.compsetMedian)
          )}
        </Figure>
        <Figure label="Gap" sub={night.gapAmount != null ? signedMoney(night.gapAmount, currency) : undefined}>
          {night.gapLabel ?? <span className="text-sm font-normal text-muted-foreground">--</span>}
        </Figure>
        <Figure label="Rank">
          {night.rankLabel ?? <span className="text-sm font-normal text-muted-foreground">--</span>}
        </Figure>
        <Figure
          label="On the books"
          sub={`${night.roomsOnBooks} of ${night.roomsTotal} rooms`}
          title={night.occupancyLabel ?? undefined}
          className={showBookingCom ? undefined : 'col-span-2 sm:col-span-1'}
        >
          {night.occupancyPct == null ? '--' : `${night.occupancyPct}%`}
        </Figure>
        {showBookingCom && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  tabIndex={0}
                  className="min-w-0 cursor-help bg-white px-3.5 py-2.5 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
                >
                  <dt className="truncate text-[11px] font-medium text-muted-foreground">On Booking.com</dt>
                  <dd className="mt-0.5 truncate text-base font-medium tabular-nums text-slate-600">
                    {night.yourBookingComPrice != null ? money(night.yourBookingComPrice) : 'Sold out'}
                  </dd>
                </div>
              </TooltipTrigger>
              {night.yourBookingComSentence && (
                <TooltipContent className="max-w-xs text-xs">{night.yourBookingComSentence}</TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}
      </dl>

      <NightLineup
        night={night}
        competitors={competitors}
        numbers={numbers}
        youName={youName}
        currency={currency}
        domain={domain}
        reducedMotion={reducedMotion}
        onKeyDown={onLineupKeyDown}
      />
    </section>
  )
}
